import { motion } from 'framer-motion';
import {
  ArrowRight,
  BarChart3,
  Calculator,
  Calendar,
  Home,
  IndianRupee,
  Percent,
  Shield,
  Target,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { useState } from 'react';

import { Footer } from '@components/Footer';
import { Header } from '@components/Header';
import { SEOHead } from '@components/SEOHead';

import { Button } from '@ui/Button';
import { Hero } from '@ui/Hero';

import { COLORS } from '@constants/colors';
import { CONTACT_INFO } from '@constants/contact';

export const ToolsPage = () => {
  // Financial Tools Data
  const financialCalculators = [
    {
      id: 'goal-planner',
      title: 'Goal planner',
      description:
        'Plan your financial goals like retirement, child education, or home purchase',
      icon: Target,
      category: 'Financial planning',
      features: [
        'Multiple goal tracking',
        'Timeline planning',
        'Investment strategy',
      ],
      comingSoon: false,
      cta: 'Start planning',
      url: '/tools/goal-planner',
    },
    {
      id: 'retirement-planner',
      title: 'Retirement planner',
      description:
        'Plan your retirement corpus and monthly pension requirements with comprehensive projections',
      icon: Calendar,
      category: 'Retirement planning',
      features: [
        'Corpus calculation',
        'Inflation adjustment',
        'Withdrawal strategy',
      ],
      comingSoon: false,
      cta: 'Start planning',
      url: '/tools/retirement-planner',
    },
    {
      id: 'risk-profiler',
      title: 'Risk profiler',
      description:
        'Assess your risk tolerance and get personalized investment recommendations',
      icon: Shield,
      category: 'Risk assessment',
      features: [
        'Risk assessment quiz',
        'Portfolio allocation',
        'Personalized advice',
      ],
      comingSoon: false,
      cta: 'Assess your risk profile',
      url: '/tools/risk-profiler',
    },
    {
      id: 'sip-delay-calculator',
      title: 'SIP delay cost calculator',
      description:
        'Calculate the cost of delaying your SIP investments and see how procrastination affects wealth creation',
      icon: Calendar,
      category: 'Investment planning',
      features: [
        'Delay impact calculation',
        'Procrastination cost analysis',
        'Extra investment needed',
      ],
      comingSoon: false,
      cta: 'Use calculator',
      url: '/tools/sip-delay-calculator',
    },
    {
      id: 'sip-calculator',
      title: 'SIP calculator',
      description:
        'Calculate your systematic investment plan returns and see how your money grows over time',
      icon: Calculator,
      category: 'Investment planning',
      features: [
        'Monthly SIP calculation',
        'Step-up SIP calculation',
        'Compound interest visualization',
      ],
      comingSoon: false,
      cta: 'Use calculator',
      url: '/tools/sip-calculator',
    },
    {
      id: 'swp-calculator',
      title: 'SWP calculator',
      description:
        'Calculate your systematic withdrawal plan and see how long your corpus will last with regular withdrawals',
      icon: TrendingDown,
      category: 'Retirement planning',
      features: [
        'Monthly withdrawal calculation',
        'Step-up withdrawal planning',
        'Corpus depletion analysis',
      ],
      comingSoon: false,
      cta: 'Use calculator',
      url: '/tools/swp-calculator',
    },
    {
      id: 'lumpsum-calculator',
      title: 'Lumpsum calculator',
      description:
        'Calculate returns on one-time investments in any asset class',
      icon: IndianRupee,
      category: 'Investment planning',
      features: [
        'One-time investment returns',
        'Growth projection',
        'Compound interest visualization',
      ],
      comingSoon: false,
      cta: 'Use calculator',
      url: '/tools/lumpsum-calculator',
    },
    {
      id: 'tax-calculator',
      title: 'Tax calculator',
      description:
        'Calculate tax savings through ELSS and other tax-saving investments',
      icon: Percent,
      category: 'Tax planning',
      features: [
        '80C ELSS deductions',
        'Old regime deductions',
        'New regime rebates (Budget 2025)',
      ],
      comingSoon: false,
      cta: 'Use calculator',
      url: '/tools/tax-calculator',
    },
    {
      id: 'home-loan-calculator',
      title: 'Home loan calculator',
      description:
        'Calculate EMI, total interest, and create amortization schedule with prepayment options',
      icon: Home,
      category: 'Loan planning',
      features: [
        'EMI calculation',
        'Prepayment analysis',
        'Amortization schedule',
      ],
      comingSoon: false,
      cta: 'Use calculator',
      url: '/tools/home-loan-calculator',
    },
    {
      id: 'profit-loss-calculator',
      title: 'Profit/loss calculator',
      description:
        'Calculate profit or loss from your stock and mutual fund investments with detailed charge breakdown',
      icon: TrendingUp,
      category: 'Investment planning',
      features: [
        'Stock & mutual fund P&L',
        'Charge breakdown',
        'Tax calculations',
      ],
      comingSoon: false,
      cta: 'Use calculator',
      url: '/tools/profit-loss-calculator',
    },
    {
      id: 'cagr-calculator',
      title: 'CAGR calculator',
      description:
        'Calculate compound annual growth rate (CAGR) to measure your investment performance over time',
      icon: BarChart3,
      category: 'Investment planning',
      features: [
        'CAGR calculation',
        'Investment performance analysis',
        'Multiple time period support',
      ],
      comingSoon: false,
      cta: 'Use calculator',
      url: '/tools/cagr-calculator',
    },
  ];

  const [selectedCategory, setSelectedCategory] = useState('All');
  const categories = [
    'All',
    'Financial planning',
    'Retirement planning',
    'Risk assessment',
    'Investment planning',
    'Tax planning',
    'Loan planning',
  ];

  const filteredTools =
    selectedCategory === 'All'
      ? financialCalculators
      : financialCalculators.filter(tool => tool.category === selectedCategory);

  return (
    <>
      <SEOHead
        title={`Financial tools & calculators | ${CONTACT_INFO.company.name}`}
        description="Free financial calculators and tools for SIP, SWP, Profit/Loss, Lumpsum, Home Loan, Goal planning, Tax calculation, Retirement planning, Risk assessment, Home Loan, etc. Make informed investment decisions."
        keywords="SIP calculator, SWP calculator, Profit/loss calculator, Lumpsum calculator, Home Loan calculator, Goal planning, Tax calculation, Retirement planning, Risk assessment"
        canonicalUrl="https://niveshright.com/tools"
      />

      <div className="min-h-screen" style={{ backgroundColor: COLORS.pearl }}>
        <Header />

        <Hero
          title={
            <>
              Financial tools &{' '}
              <span style={{ color: COLORS.gold }}>calculators</span>
            </>
          }
          description="Make informed investment decisions with our comprehensive suite of financial calculators and planning tools"
          variant="gradient"
        />

        {/* Category Filter */}
        <motion.section
          className="py-8"
          style={{ backgroundColor: COLORS.mist }}
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
              {categories.map(category => (
                <Button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  variant={selectedCategory === category ? 'accent' : 'ghost'}
                  shape="pill"
                  size="sm"
                  animate={false}
                  className="sm:text-base sm:px-6 sm:py-3 sm:min-h-[2.5rem]"
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Financial Calculators Grid */}
        <motion.section
          className="py-12"
          style={{ backgroundColor: COLORS.pearl }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredTools.map((tool, index) => {
                const IconComponent = tool.icon;
                return (
                  <motion.div
                    key={tool.id}
                    className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 flex flex-col h-full"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: index * 0.1 }}
                    whileHover={{ y: -2 }}
                  >
                    {tool.comingSoon && (
                      <div className="absolute top-4 right-4 bg-gradient-to-r from-orange-400 to-orange-500 text-white text-xs px-3 py-1 rounded-full font-medium">
                        Coming Soon
                      </div>
                    )}

                    <div className="flex items-center mb-6">
                      <div
                        className="w-16 h-16 rounded-2xl flex items-center justify-center mr-4"
                        style={{ backgroundColor: `${COLORS.accent}15` }}
                      >
                        <IconComponent
                          size={32}
                          style={{ color: COLORS.accent }}
                        />
                      </div>
                      <div>
                        <h3
                          className="text-xl font-bold"
                          style={{ color: COLORS.primary }}
                        >
                          {tool.title}
                        </h3>
                        <p className="text-sm" style={{ color: COLORS.accent }}>
                          {tool.category}
                        </p>
                      </div>
                    </div>

                    <div className="flex-grow">
                      <p className="text-gray-600 mb-6 leading-relaxed">
                        {tool.description}
                      </p>

                      <div className="space-y-2 mb-6">
                        {tool.features.map((feature, idx) => (
                          <div
                            key={idx}
                            className="flex items-center text-sm text-gray-500"
                          >
                            <div
                              className="w-1.5 h-1.5 rounded-full mr-3"
                              style={{ backgroundColor: COLORS.sage }}
                            ></div>
                            {feature}
                          </div>
                        ))}
                      </div>
                    </div>

                    {tool.comingSoon ? (
                      <Button
                        variant="ghost"
                        fullWidth
                        disabled={true}
                        animate={false}
                      >
                        Coming Soon
                      </Button>
                    ) : (
                      <Button
                        href={tool.url}
                        variant="accent"
                        fullWidth
                        icon={ArrowRight}
                        iconSize={16}
                      >
                        {tool.cta}
                      </Button>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section
          className="py-20"
          style={{ backgroundColor: COLORS.pearl }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.4 }}
        >
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2
              className="text-4xl font-bold mb-6"
              style={{ color: COLORS.primary }}
            >
              Need personalized advice?
            </h2>
            <p className="text-xl mb-8" style={{ color: COLORS.smoke }}>
              While our tools provide great insights, nothing beats personalized
              financial advice. Connect with our experts for a customized
              investment strategy.
            </p>
            <Button
              href={`https://wa.me/${CONTACT_INFO.phone.whatsapp}?text=Hi ${CONTACT_INFO.contact_person.short_name}, I used your financial tools and would like personalized investment advice.`}
              target="_blank"
              rel="noopener noreferrer"
              variant="sage"
              size="lg"
              icon={ArrowRight}
              iconSize={20}
            >
              Get personalized advice
            </Button>
          </div>
        </motion.section>

        <Footer />
      </div>
    </>
  );
};
